import {
  Box,
  Typography,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputLabel,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import {
  Add,
  Close,
  AddCircleOutlineOutlined,
  DeleteOutline,
} from "@mui/icons-material";
import { useState, useEffect, useCallback } from "react";
import { tokens } from "../../../theme";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";
import PenPaperIcon from "../../../components/icons/PenPaperIcon";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";
// ─── Types ────────────────────────────────────────────────────────────────────

interface Component {
  id: number;
  name: string;
  description?: string;
}

interface User {
  id: number;
  username: string;
  first_name?: string;
  last_name?: string;
}

type Status = "in-progress" | "complete" | "overdue";

interface UpcomingFeature {
  id: number;
  name?: string;
  component: Component;
  lead_developer: User;
  backup_developer?: User | null;
  description?: string | null;
  staging_date?: string | null;
  live_date?: string | null;
  status: Status;
}

interface FeatureForm {
  component_id: string;
  lead_developer_id: string;
  description: string;
  staging_date: string;
  live_date: string;
  status: Status;
}

const emptyForm: FeatureForm = {
  component_id: "",
  lead_developer_id: "",
  description: "",
  staging_date: "",
  live_date: "",
  status: "in-progress",
};

const STATUSES: { value: Status; label: string; color: string }[] = [
  { value: "in-progress", label: "In Progress", color: "#d49114" },
  { value: "complete", label: "Complete", color: "#0c8342" },
  { value: "overdue", label: "Overdue", color: "#E11D48" },
];

// ─── Component ────────────────────────────────────────────────────────────────

const UpcomingFeaturesConfig = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { hasPermission } = usePermissions();
  const canAdd = hasPermission(PERMISSIONS.CONFIGURATION_ADD);
  const canEdit = hasPermission(PERMISSIONS.CONFIGURATION_CHANGE);
  const canDelete = hasPermission(PERMISSIONS.CONFIGURATION_DELETE);

  const [features, setFeatures] = useState<UpcomingFeature[]>([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);

  const [components, setComponents] = useState<Component[]>([]);
  const [loadingComponents, setLoadingComponents] = useState(false);

  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Add / Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState<UpcomingFeature | null>(
    null,
  );
  const [form, setForm] = useState<FeatureForm>(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<UpcomingFeature | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);

  // Add component sub-dialog
  const [showAddComponent, setShowAddComponent] = useState(false);
  const [newComponentData, setNewComponentData] = useState({
    name: "",
    description: "",
  });
  const [savingComponent, setSavingComponent] = useState(false);

  // ─── Fetch helpers ────────────────────────────────────────────────────────

  const fetchPaginated = async <T,>(url: string): Promise<T[]> => {
    let all: T[] = [];
    let next: string | null = url;
    while (next) {
      const res: { data: { results?: T[]; next?: string | null } | T[] } =
        await apiService.get(next);
      if (Array.isArray(res.data)) {
        all = res.data;
        break;
      }
      const paged = res.data as { results?: T[]; next?: string | null };
      all = [...all, ...(paged.results ?? [])];
      if (paged.next) {
        const u = new URL(paged.next);
        next = u.pathname + u.search;
      } else {
        next = null;
      }
    }
    return all;
  };

  const loadFeatures = useCallback(async () => {
    setLoadingFeatures(true);
    try {
      const data = await fetchPaginated<UpcomingFeature>(
        "/api/configurations/upcoming-features/",
      );
      setFeatures(data);
    } catch {
      toast.error("Failed to load upcoming features.");
    } finally {
      setLoadingFeatures(false);
    }
  }, []);

  const loadComponents = useCallback(async () => {
    setLoadingComponents(true);
    try {
      const data = await fetchPaginated<Component>(
        "/api/endpoint-request/components/",
      );
      setComponents(data);
    } catch {
      toast.error("Failed to load components.");
    } finally {
      setLoadingComponents(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const data = await fetchPaginated<User>(
        "/api/authentication/users/developers/",
      );
      setUsers(data);
    } catch {
      toast.error("Failed to load users.");
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    loadFeatures();
    loadComponents();
    loadUsers();
  }, [loadFeatures, loadComponents, loadUsers]);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const getUserLabel = (u: User) =>
    [u.first_name, u.last_name].filter(Boolean).join(" ") || u.username;

  const openAdd = () => {
    setEditingFeature(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (f: UpcomingFeature) => {
    setEditingFeature(f);
    setForm({
      component_id: f.component.id.toString(),
      lead_developer_id: f.lead_developer.id.toString(),
      description: f.description ?? "",
      staging_date: f.staging_date ?? "",
      live_date: f.live_date ?? "",
      status: f.status,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingFeature(null);
    setForm(emptyForm);
  };

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!form.component_id || !form.lead_developer_id) {
      toast.error("Component and Lead Developer are required.");
      return;
    }

    const payload = {
      component_id: Number(form.component_id),
      lead_developer_id: Number(form.lead_developer_id),
      description: form.description || null,
      staging_date: form.staging_date || null,
      live_date: form.live_date || null,
      status: form.status,
    };

    setSubmitting(true);
    try {
      // After successful PATCH in handleSubmit:
      if (editingFeature) {
        await apiService.patch(
          `/api/configurations/upcoming-features/${editingFeature.id}/`,
          payload,
        );
        toast.success("Feature updated successfully!");
        setFeatures((prev) =>
          prev.map((f) =>
            f.id === editingFeature.id
              ? {
                  ...f,
                  component:
                    components.find(
                      (c) => c.id === Number(form.component_id),
                    ) ?? f.component,
                  lead_developer:
                    users.find(
                      (u) => u.id === Number(form.lead_developer_id),
                    ) ?? f.lead_developer,
                  description: form.description || null,
                  staging_date: form.staging_date || null,
                  live_date: form.live_date || null,
                  status: form.status,
                }
              : f,
          ),
        );
      } else {
        const res = await apiService.post<UpcomingFeature>(
          "/api/configurations/upcoming-features/",
          payload,
        );
        toast.success("Feature added successfully!");
        setFeatures((prev) => [...prev, res.data]);
      }
      closeModal();
    } catch {
      toast.error("Failed to save feature.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiService.delete(
        `/api/configurations/upcoming-features/${deleteTarget.id}/`,
      );
      toast.success("Feature removed.");
      setFeatures((prev) => prev.filter((f) => f.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      toast.error("Failed to remove feature.");
    } finally {
      setDeleting(false);
    }
  };

  // ─── Add Component ────────────────────────────────────────────────────────

  const handleSaveComponent = async () => {
    if (!newComponentData.name.trim()) {
      toast.error("Component name is required.");
      return;
    }
    setSavingComponent(true);
    try {
      const res = await apiService.post<Component>(
        "/api/endpoint-request/components/",
        {
          name: newComponentData.name.trim(),
          description: newComponentData.description.trim() || "",
        },
      );
      const saved = res.data;
      toast.success("Component added!");
      setComponents((prev) => [...prev, saved]);
      setForm((prev) => ({ ...prev, component_id: saved.id.toString() }));
      setShowAddComponent(false);
      setNewComponentData({ name: "", description: "" });
    } catch {
      toast.error("Failed to add component.");
    } finally {
      setSavingComponent(false);
    }
  };

  // ─── Table row styles ─────────────────────────────────────────────────────

  const rowSx = {
    display: "grid",
    gridTemplateColumns: "2fr 1.2fr 1fr 1fr 1.2fr auto",
    gap: 1.5,
    alignItems: "center",
    px: 2.5,
    py: 1.5,
  };
  const statusColor = (s: Status) =>
    STATUSES.find((x) => x.value === s)?.color ?? colors.primary[400];

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        px: 2.5,
        py: 2.5,
        borderRadius: "24px",
        backgroundColor: colors.primary[900],
        border: `1px solid ${colors.primary[300]}30`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>
          Upcoming Features
        </Typography>
        {canAdd && (
          <Button
            size="small"
            startIcon={<Add />}
            onClick={openAdd}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              fontSize: "0.78rem",
              border: `1px solid ${colors.primary[400]}`,
              color: colors.primary[100],
            }}
          >
            Add Feature
          </Button>
        )}
      </Box>
      {/* Table */}
      <Box>
        {/* Column headers */}
        <Box sx={{ ...rowSx, backgroundColor: colors.primary[800] }}>
          {["Component", "Lead", "Staging", "Live Date", "Status", ""].map(
            (h) => (
              <Typography
                key={h}
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: colors.primary[700],
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                }}
              >
                {h}
              </Typography>
            ),
          )}
        </Box>

        {/* Rows */}
        {loadingFeatures ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : features.length === 0 ? (
          <Typography
            sx={{
              textAlign: "center",
              color: colors.primary[600],
              py: 4,
              fontSize: "0.85rem",
            }}
          >
            No features yet. Add one to get started.
          </Typography>
        ) : (
          features.map((f, i) => (
            <Box
              key={f.id}
              sx={{
                ...rowSx,
                borderTop:
                  i === 0 ? "none" : `1px solid ${colors.primary[300]}20`,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: colors.primary[100],
                }}
              >
                {f.component.name}
              </Typography>
              <Typography
                sx={{ fontSize: "0.82rem", color: colors.primary[400] }}
              >
                {getUserLabel(f.lead_developer)}
              </Typography>
              <Typography
                sx={{ fontSize: "0.82rem", color: colors.primary[400] }}
              >
                {f.staging_date || "—"}
              </Typography>
              <Typography
                sx={{ fontSize: "0.82rem", color: colors.primary[400] }}
              >
                {f.live_date || "—"}
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  width: "fit-content",
                  px: 1.25,
                  py: 0.35,
                  borderRadius: "999px",
                  backgroundColor: `${statusColor(f.status)}1A`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    color: statusColor(f.status),
                  }}
                >
                  {STATUSES.find((s) => s.value === f.status)?.label ??
                    f.status}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}
              >
                {canEdit && (
                  <IconButton size="small" onClick={() => openEdit(f)}>
                    <PenPaperIcon
                      sx={{ fontSize: 15, color: colors.primary[500] }}
                    />
                  </IconButton>
                )}
                {canDelete && (
                  <IconButton size="small" onClick={() => setDeleteTarget(f)}>
                    <DeleteOutline sx={{ fontSize: 15, color: "#E11D48" }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          ))
        )}
      </Box>

      {/* ── Add / Edit Modal ─────────────────────────────────────────────── */}
      <Dialog
        open={showModal}
        onClose={closeModal}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            backgroundColor: colors.primary[900],
            border: `1px solid ${colors.primary[300]}30`,
            minWidth: 460,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Typography sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>
            {editingFeature ? "Edit Feature" : "Add Feature"}
          </Typography>
          <IconButton size="small" onClick={closeModal}>
            <Close sx={{ fontSize: 16, color: colors.primary[600] }} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "12px !important",
          }}
        >
          {/* Component selector + Add Component button */}
          <Box>
            <FormControl size="small" fullWidth disabled={loadingComponents}>
              <InputLabel>Component *</InputLabel>
              <Select
                label="Component *"
                value={form.component_id}
                onChange={(e) =>
                  setForm((p) => ({ ...p, component_id: e.target.value }))
                }
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 280 },
                  },
                }}
              >
                {components.map((c) => (
                  <MenuItem key={c.id} value={c.id.toString()}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              startIcon={<AddCircleOutlineOutlined />}
              onClick={() => {
                setNewComponentData({ name: "", description: "" });
                setShowAddComponent(true);
              }}
              sx={{
                mt: 0.75,
                textTransform: "none",
                fontSize: "0.75rem",
                color: colors.primary[400],
              }}
            >
              Add Component
            </Button>
          </Box>

          {/* Lead Developer */}
          <Autocomplete
            size="small"
            fullWidth
            options={users}
            loading={loadingUsers}
            getOptionLabel={getUserLabel}
            filterOptions={(options, { inputValue }) =>
              inputValue.trim() === ""
                ? []
                : options.filter((u) =>
                    getUserLabel(u)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase()),
                  )
            }
            noOptionsText="Type to search…"
            value={
              users.find((u) => u.id.toString() === form.lead_developer_id) ??
              null
            }
            onChange={(_, val) =>
              setForm((p) => ({
                ...p,
                lead_developer_id: val ? val.id.toString() : "",
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Lead Developer *"
                placeholder="Type to search…"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Description */}
          <TextField
            size="small"
            fullWidth
            label="Description"
            multiline
            rows={2}
            value={form.description}
            onChange={(e) =>
              setForm((p) => ({ ...p, description: e.target.value }))
            }
          />

          {/* Staging Date */}
          <TextField
            size="small"
            fullWidth
            label="Staging Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.staging_date}
            onChange={(e) =>
              setForm((p) => ({ ...p, staging_date: e.target.value }))
            }
          />

          {/* Live Date */}
          <TextField
            size="small"
            fullWidth
            label="Live Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.live_date}
            onChange={(e) =>
              setForm((p) => ({ ...p, live_date: e.target.value }))
            }
          />

          {/* Status */}
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={form.status}
              onChange={(e) =>
                setForm((p) => ({ ...p, status: e.target.value as Status }))
              }
            >
              {STATUSES.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: s.color,
                      }}
                    />
                    {s.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            size="small"
            onClick={closeModal}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              color: colors.primary[400],
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              fontSize: "0.82rem",
            }}
          >
            {submitting ? (
              <>
                <CircularProgress size={14} sx={{ mr: 1 }} />
                {editingFeature ? "Updating…" : "Adding…"}
              </>
            ) : editingFeature ? (
              "Update Feature"
            ) : (
              "Add Feature"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation ──────────────────────────────────────────── */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            backgroundColor: colors.primary[900],
            border: `1px solid ${colors.primary[300]}30`,
          },
        }}
      >
        <DialogTitle>Remove Feature</DialogTitle>
        <DialogContent>
          <Typography sx={{ py: 1, fontSize: "0.9rem" }}>
            Are you sure you want to remove{" "}
            <strong>{deleteTarget?.component.name}</strong>? This cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            size="small"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            sx={{ borderRadius: "20px", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ borderRadius: "20px", textTransform: "none" }}
          >
            {deleting ? (
              <>
                <CircularProgress size={14} sx={{ mr: 1 }} />
                Removing…
              </>
            ) : (
              "Remove"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add Component Sub-dialog ─────────────────────────────────────── */}
      <Dialog
        open={showAddComponent}
        onClose={() => setShowAddComponent(false)}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            backgroundColor: colors.primary[900],
            border: `1px solid ${colors.primary[300]}30`,
            minWidth: 380,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Typography sx={{ fontWeight: "bold", fontSize: "0.95rem" }}>
            Add Component
          </Typography>
          <IconButton size="small" onClick={() => setShowAddComponent(false)}>
            <Close sx={{ fontSize: 16, color: colors.primary[600] }} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "12px !important",
          }}
        >
          <TextField
            autoFocus
            size="small"
            fullWidth
            label="Component Name *"
            value={newComponentData.name}
            onChange={(e) =>
              setNewComponentData((p) => ({ ...p, name: e.target.value }))
            }
          />
          <TextField
            size="small"
            fullWidth
            label="Description"
            multiline
            rows={3}
            value={newComponentData.description}
            onChange={(e) =>
              setNewComponentData((p) => ({
                ...p,
                description: e.target.value,
              }))
            }
            placeholder="Describe what this component is used for…"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            size="small"
            onClick={() => setShowAddComponent(false)}
            disabled={savingComponent}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              color: colors.primary[400],
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSaveComponent}
            disabled={savingComponent || !newComponentData.name.trim()}
            sx={{
              borderRadius: "20px",
              textTransform: "none",
              fontSize: "0.82rem",
            }}
          >
            {savingComponent ? (
              <>
                <CircularProgress size={14} sx={{ mr: 1 }} />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UpcomingFeaturesConfig;
